import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import BriefForm from "@/components/BriefForm";
import api from "@/lib/api";
import { CONTACT } from "@/data/site";
import { Check, MapPin, Users, ShieldCheck, UserCheck } from "lucide-react";

const BENEFITS = ["Échange de cadrage sous 24 h ouvrées", "3 scénarios budgétaires — Essentiel, Signature, Prestige", "Un interlocuteur unique dédié", "Sans engagement"];
const BUDGET_REPERES = [
  { label: "Cocktail / soirée (100-200 pers.)", range: "15 000 – 60 000 €" },
  { label: "Séminaire journée", range: "150 – 350 € / pers." },
  { label: "Inauguration (50-300 pers.)", range: "5 000 – 25 000 €" },
];

function Proofs() {
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/projects").then((r) => setProjects((r.data || []).slice(0, 3))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials((r.data || []).slice(0, 3))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  return (
    <section className="border-t border-ink/10 bg-mist" data-testid="devis-preuves">
      <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-20">
        <Reveal><span className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Avant de nous confier votre projet</span></Reveal>
        <Reveal delay={1}><h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Des événements réels, un interlocuteur identifié.</h2></Reveal>

        {/* Réalisations réelles */}
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={i}>
              <Link to="/nos-realisations" className="group block h-full overflow-hidden rounded-2xl border border-ink/10 bg-white" data-testid={`devis-preuve-${i}`}>
                <img src={p.image} alt={p.title} className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="p-5">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-lime-dim">{p.category}</p>
                  <h3 className="mt-2 font-serif text-lg leading-snug text-ink">{p.title}</h3>
                  <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink/50">
                    {p.city && <span className="flex items-center gap-1"><MapPin size={12} />{p.city}</span>}
                    {p.guests && <span className="flex items-center gap-1"><Users size={12} />{p.guests}</span>}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Avis clients réels — affichés uniquement s'ils existent */}
        {testimonials.length > 0 && (
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-testid="devis-avis">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i}>
                <figure className="h-full rounded-2xl border border-ink/10 bg-white p-6">
                  <blockquote className="text-sm leading-relaxed text-ink/70">« {t.text} »</blockquote>
                  <figcaption className="mt-4 text-xs text-ink/50">{t.author}{t.role ? ` — ${t.role}` : ""}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        {/* Interlocuteur + informations légales */}
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <Reveal>
            <div className="flex h-full items-start gap-5 rounded-2xl border border-ink/10 bg-white p-7" data-testid="devis-interlocuteur">
              {settings.founder_photo ? (
                <img src={settings.founder_photo} alt={settings.founder_name || "Votre interlocuteur"} className="h-16 w-16 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><UserCheck size={26} /></span>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-ink/40">Votre interlocuteur</p>
                <h3 className="mt-1 font-serif text-xl text-ink">{settings.founder_name || "Mathias Désir"}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">
                  {settings.founder_role || "Fondateur et chef de projet"} — {settings.stats_years || "7 ans"} d'expérience en événementiel d'entreprise.
                  C'est lui qui étudie votre brief, vous rappelle et coordonne votre événement jusqu'au jour J.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="flex h-full items-start gap-5 rounded-2xl border border-ink/10 bg-white p-7" data-testid="devis-legal">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-ink text-lime"><ShieldCheck size={26} /></span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-ink/40">Une société identifiée</p>
                <h3 className="mt-1 font-serif text-xl text-ink">{CONTACT.legalName}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">
                  SIRET {CONTACT.siret} — {CONTACT.address}.<br />
                  {CONTACT.phoneFixe} · {CONTACT.phoneMobile} · {CONTACT.email}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Devis() {
  return (
    <>
      <Seo title="Commencer mon brief — 2 minutes" description="Décrivez votre événement d'entreprise en 5 étapes : première fourchette budgétaire immédiate, échange de cadrage sous 24 h ouvrées, puis 3 scénarios chiffrés par votre chef de projet." path="/devis" />
      <PageHero eyebrow="Commencer mon brief" title={<>Décrivez votre événement.<br /><span className="italic text-lime-dim">Nous l'organisons de A à Z.</span></>} />

      {/* Repères budgétaires */}
      <section className="mx-auto max-w-[1440px] px-5 pt-14 md:px-10" data-testid="devis-budget-reperes">
        <Reveal><span className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Repères budgétaires</span></Reveal>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {BUDGET_REPERES.map((b) => (
            <div key={b.label} className="rounded-2xl border border-ink/10 bg-white p-6">
              <p className="text-sm text-ink/55">{b.label}</p>
              <p className="mt-2 font-serif text-2xl text-ink">{b.range}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 max-w-2xl text-xs text-ink/45">Fourchettes indicatives de nos formats habituels. Votre devis détaille chaque poste, sans engagement.</p>
      </section>

      {/* Preuves AVANT le formulaire */}
      <div className="mt-16"><Proofs /></div>

      {/* Formulaire progressif */}
      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-4">
            <Reveal><p className="font-serif text-2xl leading-snug text-ink">5 étapes, 2 minutes. Une première fourchette budgétaire s'affiche dès l'envoi — puis votre chef de projet cale tout avec vous.</p></Reveal>
            <ul className="mt-10 space-y-4">
              {BENEFITS.map((b, i) => (
                <Reveal key={b} delay={i}>
                  <li className="flex items-center gap-3 text-ink/70">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><Check size={14} /></span>{b}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <div className="rounded-2xl border border-ink/10 bg-mist p-8 md:p-10">
              <BriefForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
